import {
  type DateTimeConstraints,
  type PhoneConstraints,
  Format,
} from '@wix/ambassador-forms-v4-form/types';
import { asOptional } from '../../utils/lang';
import dayJs, { type UnitTypeShort, type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { CALLING_COUNTRY_CODES } from '../../calling-country-codes';

dayJs.extend(utc);
const newDate = dayJs.utc;

const RELATIVE_FORMAT_REGEX = /^\$now(?:([+-])(\d{1,2})([yMdmh]))?$/;
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const ISO_TIME_REGEX = /^\d{2}:\d{2}:\d{2}(\.\d+)?$/;
const ISO_DATE_TIME_REGEX =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;

export function stringFormatOptionsMapper(
  format: Format,
  dateFormatOptions?: DateTimeConstraints,
  dateFactory: () => Date = () => new Date(),
  phoneFormatOptions?: PhoneConstraints,
  libraryBasedPhoneFieldValidationEnabled: boolean = false,
): {
  formatMinimum?: string;
  formatMaximum?: string;
  phone?: boolean;
  allowedCountryCodes?: string[];
} {
  const stringDateFormatOptions = {
    formatMinimum: parseIfNeeded(
      format,
      dateFactory,
      asOptional(dateFormatOptions?.minimum),
    ),
    formatMaximum: parseIfNeeded(
      format,
      dateFactory,
      asOptional(dateFormatOptions?.maximum),
    ),
  };

  if (libraryBasedPhoneFieldValidationEnabled) {
    return {
      ...stringDateFormatOptions,
      phone: format === 'PHONE' ? true : undefined,
      allowedCountryCodes:
        format === 'PHONE'
          ? phoneFormatOptions?.allowedCountryCodes?.length
            ? phoneFormatOptions.allowedCountryCodes
            : Object.keys(CALLING_COUNTRY_CODES)
          : undefined,
    };
  } else {
    return stringDateFormatOptions;
  }
}

function parseIfNeeded(
  format: Format,
  dateProvider: () => Date,
  value?: string,
): string | undefined {
  const currentDate: Dayjs = newDate(dateProvider().toISOString());
  if (!value) {
    return value;
  }
  if (isStaticDateOrTime(value)) {
    return value;
  }
  if (isRelativeDate(value)) {
    return mapToStaticDate(value, currentDate)
      ?.utc()
      .format(asIsoFormat(format));
  }
  return undefined;
}

function isRelativeDate(value: string): boolean {
  return RELATIVE_FORMAT_REGEX.test(value);
}

function isStaticDateOrTime(value: string): boolean {
  return (
    ISO_DATE_REGEX.test(value) ||
    ISO_TIME_REGEX.test(value) ||
    ISO_DATE_TIME_REGEX.test(value)
  );
}

function mapToStaticDate(value: string, currentDate: Dayjs): Dayjs | undefined {
  if (value === '$now') {
    return currentDate;
  }

  try {
    const [, operator, amountStr, unitStr] =
      value.match(RELATIVE_FORMAT_REGEX) ?? [];
    const amount = parseAmount(amountStr);
    const unit = parseUnitType(unitStr);
    if (amount && unit) {
      const setDate = parseAction(currentDate, operator);
      return setDate(amount, unit);
    }
    return undefined;
  } catch (e) {
    console.error(`Failed to parse format ${value}`, e);
    return undefined;
  }
}

function parseAmount(amount: string): number | undefined {
  const number = parseInt(amount, 10);
  return isNaN(number) ? undefined : number;
}

function parseAction(
  currentDate: Dayjs,
  operator: string,
): (amount: number, unit: UnitTypeShort) => Dayjs {
  switch (operator) {
    case '+':
      return currentDate.add.bind(currentDate);
    case '-':
      return currentDate.subtract.bind(currentDate);
    default:
      return () => currentDate;
  }
}

function parseUnitType(unit: string): UnitTypeShort | undefined {
  const supportedUnits: UnitTypeShort[] = ['d', 'M', 'y', 'h', 'm'];
  if (supportedUnits.includes(unit as UnitTypeShort)) {
    return unit as UnitTypeShort;
  }
  return undefined;
}

function asIsoFormat(format: Format): string | undefined {
  switch (format) {
    case Format.DATE:
      return 'YYYY-MM-DD';
    case Format.TIME:
      return 'HH:mm:ss';
    default:
      return undefined;
  }
}
